import { ContextualVisualGuideSchema, type ContextualVisualGuide } from './ContextualVisual';

/** Read completed JSON values from a bounded prefix. Never completes strings, numbers,
 * or array entries speculatively; a half-written mark/part is not drawable. */
export function contextualVisualProgress(text: string): ContextualVisualGuide | null {
  if (text.length > 64_000) return null;
  let index = 0;
  const space = () => { while (/\s/.test(text[index] ?? '') && index < text.length) index++; };
  type Result = { value: unknown; complete: boolean };
  const read = (depth = 0): Result => {
    space(); if (depth > 12 || index >= text.length) return { value: undefined, complete: false };
    const ch = text[index];
    if (ch === '"') {
      const start = index++;
      while (index < text.length) {
        if (text[index] === '\\') { index += 2; continue; }
        if (text[index++] === '"') return { value: JSON.parse(text.slice(start, index)), complete: true };
      }
      return { value: undefined, complete: false };
    }
    if (ch === '[' || ch === '{') {
      index++; const array = ch === '['; const value: unknown[] | Record<string, unknown> = array ? [] : Object.create(null);
      while (index < text.length) {
        space(); if (text[index] === (array ? ']' : '}')) { index++; return { value, complete: true }; }
        let key = '';
        if (!array) {
          const property = read(depth + 1);
          if (!property.complete || typeof property.value !== 'string') return { value, complete: false };
          key = property.value; space(); if (text[index++] !== ':') return { value, complete: false };
        }
        const item = read(depth + 1);
        if (array) { if (item.complete) (value as unknown[]).push(item.value); }
        else if (item.value !== undefined) (value as Record<string, unknown>)[key] = item.value;
        if (!item.complete) return { value, complete: false };
        space(); if (text[index] === ',') { index++; continue; }
        if (text[index] !== (array ? ']' : '}')) return { value, complete: false };
      }
      return { value, complete: false };
    }
    const start = index;
    while (index < text.length && !/[\s,\]}]/.test(text[index])) index++;
    if (index === text.length) return { value: undefined, complete: false };
    return { value: JSON.parse(text.slice(start, index)), complete: true };
  };
  try {
    const result = read();
    const value = result.value as { visualGuide?: Record<string, unknown> } | null;
    if (!value?.visualGuide) return null;
    const candidate: Record<string, unknown> = { ...value.visualGuide, marks: value.visualGuide.marks ?? [] };
    if (!result.complete) {
      // Relational metadata often precedes the parts it names in strict JSON.
      // Do not hold already-complete geometry until all controls/animation arrive.
      // Drawing snapshots cannot be interacted with or start motion; final output
      // still passes the complete guide schema, including every reference.
      delete candidate.parameters; delete candidate.motion; delete candidate.labelStates; delete candidate.readouts;
      const model = candidate.model3d as { parts?: unknown[] } | null | undefined;
      if (model && Array.isArray(model.parts) && !model.parts.length) candidate.model3d = null;
    }
    const parsed = ContextualVisualGuideSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
